import { KeyValuePipe } from '@angular/common';
import { Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_SWITCH } from '@progress/kendo-angular-inputs';
import { KENDO_GRIDLAYOUT } from '@progress/kendo-angular-layout';
import { PermissionMaskDto, PermissionMasksClient, UnityPermissionType } from '@unity/clients';
import { SortPipe } from '@unity/pipes/sort.pipe';
import { IconService } from '@unity/services/icon.service';
import { ToasterService } from '@unity/services/toaster.service';
import { defaultObjectFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema, UnityPermissionTypeStrings } from '@unity/utils/types';
import { of, take } from 'rxjs';

class PermissionStateSchema implements FormGroupControlSchema<PermissionMaskDto> {
  public role_mask_set: FormControl<Record<string, UnityPermissionType>>;

  public lineage_mask_set: FormControl<Record<string, UnityPermissionType>>;

  public constructor(dto?: PermissionMaskDto) {
    this.role_mask_set = defaultObjectFormControl<Record<string, UnityPermissionType>>({ disabled: true, value: dto?.role_mask_set });
    this.lineage_mask_set = defaultObjectFormControl<Record<string, UnityPermissionType>>({ disabled: true, value: dto?.lineage_mask_set });
  }
}

@Component({
  selector: 'unity-dashboard-permissions-shared',
  standalone: true,
  imports: [KENDO_SWITCH, KENDO_GRIDLAYOUT, KeyValuePipe, SortPipe, ReactiveFormsModule, IconsModule],
  templateUrl: './dashboard-permissions-shared.component.html',
  styleUrl: './dashboard-permissions-shared.component.scss',
})
export class DashboardPermissionsSharedComponent {
  private readonly permissionMasksClient = inject(PermissionMasksClient);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toasterService = inject(ToasterService);
  private readonly iconService = inject(IconService);

  private observer = {
    error: (e: Error) => this.toasterService.showError('Error while saving changes', e, 'error'),
    next: () => this.toasterService.showInfo(`Permissions updated`),
    complete: () => this.onEdit(false),
  };

  protected switchboardEnabled = false;
  protected permissionTypes = Object.keys(UnityPermissionType)
    // Only names, not values
    .filter(k => isNaN(Number(k)))
    // Exclude All and None from display as these have no meaning in the dashboard
    .filter(
      k => ![UnityPermissionType[UnityPermissionType.All], UnityPermissionType[UnityPermissionType.None]].includes(k)
    ) as UnityPermissionTypeStrings[];

  protected entitlementUnavailableIcon = this.iconService.xCircleIcon;
  protected permissionUnavailableIcon = this.iconService.lockIcon;

  private permissionMaskResource = rxResource({
    params: () => ({ roleId: this.$roleId() }),
    stream: ({ params: { roleId } }) => (roleId ? this.permissionMasksClient.getForRole(roleId) : of(undefined)),
  });

  protected readonly $permissionState = linkedSignal(() =>
    this.permissionMaskResource.hasValue() ? this.permissionMaskResource.value() : undefined
  );

  protected readonly $permissionsForm = computed(() => {
    const permissionState = this.$permissionState();
    const form = this.formBuilder.group<PermissionStateSchema>(new PermissionStateSchema(permissionState));
    form.disable();
    return form;
  });

  public readonly $roleId = input.required<number>({ alias: 'roleId' });
  public touchEvent = output();

  protected onSwitch(entitlement: string, actionFlag: UnityPermissionType, event: boolean) {
    const permissionState = this.$permissionState();
    const permissionForm = this.$permissionsForm();
    if (!permissionState || !permissionForm) {
      return;
    }

    const currentMask = permissionState.role_mask_set[entitlement];
    const newMask = event === true ? currentMask & ~actionFlag : currentMask | actionFlag;

    permissionState.role_mask_set[entitlement] = newMask;
    permissionForm.setValue(permissionState);
    this.$permissionState.set(permissionState);
    this.touchEvent.emit();
  }

  protected permissionType(permissionName: UnityPermissionTypeStrings) {
    return UnityPermissionType[permissionName];
  }

  protected bitEnabled(permissionValue: UnityPermissionTypeStrings, switchFlag: UnityPermissionType) {
    return (UnityPermissionType[permissionValue] & switchFlag) == 0;
  }

  protected controlBlocked(entitlement: string, switchFlag: UnityPermissionType) {
    const lineageMask = this.$permissionsForm().controls.lineage_mask_set.getRawValue();
    const blockedByLineage = (lineageMask[entitlement] & switchFlag) != 0;
    return blockedByLineage;
  }

  // eslint-disable-next-line @unity-rules/no-public-component-members
  public onEdit(event: boolean) {
    const form = this.$permissionsForm();

    if (event) {
      this.switchboardEnabled = true;
      form.enable();
    } else {
      form.disable();
      this.switchboardEnabled = false;
    }
  }

  // eslint-disable-next-line @unity-rules/no-public-component-members
  public onSave() {
    const permissionState = this.$permissionState();
    if (!permissionState) {
      return;
    }
    const roleId = this.$roleId();
    return this.permissionMasksClient.setForRole(roleId, permissionState).pipe(take(1)).subscribe(this.observer);
  }
}
